
import React from 'react';
import { Ball, SimulationConfig, Vector2D, CollisionEvent, MorphData } from '@/types';
import { ShapeUtils } from '../physics/ShapeUtils';
import { BasicLogic } from './BasicLogic';
import { TrailLogic } from './TrailLogic';
import { RhythmicLogic } from './RhythmicLogic';
import { SpawnerPhysics } from '../modes/SpawnerPhysics';
import { resolveWallCollision, resolveOutsideColliderCollision } from '../physics/BoundaryCollision';
import { handleBallCollision as handleEscapeCollision } from '../modes/escape/EscapeCollision';
import { FrameScheduler } from '@/services/FrameScheduler';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';

export const PhysicsEngine = {
  handleSubsteps: (
    deltaTime: number,
    effectiveCfg: SimulationConfig,
    balls: Ball[],
    centerX: number,
    centerY: number,
    rotRad: number,
    hue: number,
    now: number,
    currentTS: number,
    morphData: MorphData,
    collisionEvents: CollisionEvent[],
    persistentSpectrumBoost: Float32Array,
    persistentSpectrumTarget: Float32Array,
    containerRadiusRef: any,
    ballGrowthRateRef: any,
    containerShrinkRateRef: any,
    timeScaleRef: any,
    speedIncreaseRateRef: any,
    shakeRef: any,
    counterPopRef: any,
    setBounceCount: any,
    setIsPlaying: any,
    initSimulation: any,
    triggerBlast: any,
    playCollisionSound: any,
    escapeRingsRef: any,
    isRotationStopped: boolean,
    hyperSpeedState: any,
    timeRemainingRef: any,
    escapeAccelerationBoostRef: any,
    physicsSystem: any,
    ballPool: any,
    trailCircles: any[],
    trailCirclePool: any,
    workerRef: any,
    rhythmicIndexRef: any,
    rhythmicTimeScaleRef: any,
    rhythmicTimeScaleRateRef: any,
    rhythmicBreakPiecesRef: any,
    rhythmicResetTimeoutRef: any,
    simTime: number,
    refs: any
  ) => {
    const substeps = FrameScheduler.getPhysicsSubsteps(effectiveCfg.physicsSubsteps || 4);
    const physicsDt = deltaTime / substeps;
    const spawns: {pos: Vector2D, color: string}[] = [];
    const collisionEventPool = refs && refs.collisionEventPool ? refs.collisionEventPool.current : null;

    // --- Web Worker Integration ---
    if (workerRef?.current && (effectiveCfg.enableSpawner || balls.length > 20)) {
        const worker = workerRef.current as any;
        const returnedBuffer = worker.nextBuffer;
        worker.nextBuffer = null; 

        PerformanceTelemetry.recordWorkerSent();
        PerformanceTelemetry.workerMode = worker.isUsingSAB ? 'SAB' : 'TRANSFERABLE';

        worker.postMessage({ 
            type: 'STEP', 
            buffer: returnedBuffer,
            payload: { 
                dt: deltaTime, 
                substeps: substeps,
                containerRadius: containerRadiusRef.current, 
                now,
                ballGrowthRate: ballGrowthRateRef.current,
                ballGrowthAcceleration: effectiveCfg.ballGrowthAcceleration,
                ballMaxRadius: effectiveCfg.ballMaxRadius,
                ballMinRadius: effectiveCfg.ballMinRadius,
                ballGrowthMode: effectiveCfg.ballGrowthMode,
                currentTS: currentTS
            } 
        }, returnedBuffer ? [returnedBuffer.buffer] : []);
    } else {
        PerformanceTelemetry.workerActive = false;
        PerformanceTelemetry.workerMode = 'NONE';
    }

    const MS_PER_TICK = 1000 / 60;
    for (let s = 0; s < substeps; s++) {
      const stepTS = (currentTS * (deltaTime / MS_PER_TICK)) / substeps;

      for (let i = 0; i < balls.length; i++) {
        const b = balls[i];
        if (b.isBreaking) continue;
        
        BasicLogic.updateBall(b, effectiveCfg, now, hue, i, stepTS);
        
        // Manual integration for Space Spiral boxes (they don't use standard Spawner/Rhythmic loops necessarily)
        if (effectiveCfg.enableSpaceSpiral && b.isBox) {
            // Boxes in space shouldn't fall due to gravity unless desired, 
            // but the user wants "real space" behavior. Usually this means no gravity for the debris.
            // We'll apply air resistance to allow them to "settle" if hit.
            const boxAirRes = Math.pow(effectiveCfg.spaceSpiralBoxAirResistance || 1.0, stepTS);
            b.vel.x *= boxAirRes;
            b.vel.y *= boxAirRes;
            
            b.pos.x += b.vel.x * stepTS;
            b.pos.y += b.vel.y * stepTS;
        }

        TrailLogic.updateTrail(b, effectiveCfg, physicsDt, 1, currentTS, trailCircles, hue, trailCirclePool, balls.length);

        if (s === 0 && (effectiveCfg.enableTrails || effectiveCfg.trailEnabled || effectiveCfg.trailLength > 0)) {
            if (!b.trail) b.trail = [];
            b.trail.push({ ...b.pos });
            if (b.trail.length > effectiveCfg.trailLength) b.trail.shift();
        }
        
        if (!(workerRef?.current && (effectiveCfg.enableSpawner || balls.length > 20))) {
            const isRhythmic = effectiveCfg.enableRhythmic;
            const isEscape = effectiveCfg.enableEscape;

            if (isRhythmic) {
                RhythmicLogic.update(
                    b, 
                    effectiveCfg, 
                    physicsDt, 
                    centerX, 
                    centerY, 
                    containerRadiusRef, 
                    ballGrowthRateRef, 
                    rhythmicTimeScaleRef, 
                    rhythmicTimeScaleRateRef, 
                    rotRad, 
                    hue, 
                    morphData, 
                    {current: collisionEvents}, 
                    {current: persistentSpectrumBoost}, 
                    {current: persistentSpectrumTarget}, 
                    now, 
                    currentTS, 
                    (i: number) => { shakeRef.current = i; }, 
                    triggerBlast, 
                    rhythmicBreakPiecesRef, 
                    rhythmicResetTimeoutRef, 
                    rhythmicIndexRef, 
                    initSimulation, 
                    () => setBounceCount((prev: number) => prev + 1), 
                    undefined, 
                    undefined, 
                    escapeRingsRef,
                    refs.rhythmicEscapeShieldActive,
                    refs.rhythmicEscapeShieldBounces
                );
            }
            
            if (isEscape) {
                if (effectiveCfg.escapeShowOuterBorders) {
                     const impact = resolveWallCollision(b, centerX, centerY, containerRadiusRef.current, effectiveCfg, morphData, false, (pos, radius, color) => {
                        const radVal = radius * 2.5 * (effectiveCfg.collisionEffectScale || 1.0);
                        const durVal = effectiveCfg.collisionEffectDuration || 800;
                        if (collisionEventPool) {
                            collisionEvents.push(collisionEventPool.get(
                                `col-outer-${now}-${b.id}-${s}`,
                                pos,
                                now,
                                radVal,
                                color,
                                effectiveCfg.collisionEffect,
                                durVal
                            ));
                        } else {
                            collisionEvents.push({ 
                                id: `col-outer-${now}-${b.id}-${s}`, 
                                pos: { ...pos }, 
                                startTime: now, 
                                radius: radVal, 
                                color, 
                                effectType: effectiveCfg.collisionEffect, 
                                duration: durVal 
                            });
                        }
                       
                       if (effectiveCfg.rhythmicSpectrumEnabled) {
                          const hitAngle = Math.atan2(pos.y - centerY, pos.x - centerX);
                          const centerIdx = Math.floor(((hitAngle + Math.PI) / (2 * Math.PI)) * 256) % 256;
                          const spread = effectiveCfg.rhythmicSpectrumCollisionSpread || 10;
                          const intensity = effectiveCfg.rhythmicSpectrumCollisionIntensity || 1.0;
                          for (let j = -spread; j <= spread; j++) {
                              const idx = (centerIdx + j + 256) % 256;
                              const factor = 1 - Math.abs(j) / (spread + 1);
                              persistentSpectrumTarget[idx] = Math.max(persistentSpectrumTarget[idx] || 0, intensity * factor);
                          }
                       }
                    });
                    if (impact > 0.1) {
                        b.justBounced = true;
                        playCollisionSound();

                        if (effectiveCfg.ballGrowthMode === 'COLLISION') {
                            const minAllowed = effectiveCfg.ballMinRadius || 1;
                            const growth = ballGrowthRateRef.current;
                            b.radius = Math.max(minAllowed, Math.min(effectiveCfg.ballMaxRadius || 1000, b.radius + growth));
                        }
                        
                        if (effectiveCfg.speedIncreaseMode === 'COLLISION') {
                            timeScaleRef.current += speedIncreaseRateRef.current;
                        }
                        
                        if (effectiveCfg.containerShrinkMode === 'COLLISION') {
                            const shrink = containerShrinkRateRef.current * 10;
                            containerRadiusRef.current = Math.max(effectiveCfg.minContainerRadius, containerRadiusRef.current - shrink);
                        }
                    }
                }

                b.vel.y += effectiveCfg.gravity * stepTS;
                const airRes = Math.pow(effectiveCfg.airResistance || 1.0, stepTS);
                b.vel.x *= airRes; 
                b.vel.y *= airRes;
                
                if (effectiveCfg.mode !== 'SPACE_SPIRAL' && !effectiveCfg.enableRhythmic) {
                  const clamped = SpawnerPhysics.clampVelocity(b.vel, effectiveCfg);
                  b.vel.x = clamped.x;
                  b.vel.y = clamped.y;
                }

                b.pos.x += b.vel.x * stepTS; b.pos.y += b.vel.y * stepTS;

                handleEscapeCollision(
                    b,
                    escapeRingsRef,
                    effectiveCfg,
                    centerX,
                    centerY,
                    stepTS,
                    morphData,
                    rotRad,
                    (ring, idx) => {
                        b.justBounced = true;
                        if (effectiveCfg.escapeTimeChallengeMode) timeRemainingRef.current += effectiveCfg.escapeTimeAddition;
                        triggerBlast({ pos: { x: b.pos.x, y: b.pos.y }, color: b.color, radius: b.radius * 2 }, b.radius * 3, ring.angle);
                        setBounceCount((prev: number) => prev + 1);
                        counterPopRef.current = 1.35;

                        if (effectiveCfg.enableDrawer) {
                            if (!b.hitPoints) b.hitPoints = [];
                            b.hitPoints.push({ 
                                pos: { x: b.pos.x, y: b.pos.y }, 
                                time: now,
                                color: b.color,
                                velocity: Math.sqrt(b.vel.x**2 + b.vel.y**2),
                                thickness: effectiveCfg.drawerLineThickness,
                                opacity: effectiveCfg.drawerLineOpacity
                            });
                            const maxLength = effectiveCfg.drawerMaxLength || 1000;
                            if (b.hitPoints.length > maxLength) b.hitPoints.shift();
                        }
                    },
                    playCollisionSound,
                    (intensity) => { shakeRef.current = intensity; },
                    (pos, radius, color) => {
                        b.justBounced = true;
                        const radVal = radius * 2.5;
                        if (collisionEventPool) {
                            collisionEvents.push(collisionEventPool.get(
                                `col-ring-${now}-${b.id}-${s}`,
                                pos,
                                now,
                                radVal,
                                color,
                                effectiveCfg.collisionEffect,
                                800
                            ));
                        } else {
                            collisionEvents.push({ id: `col-ring-${now}-${b.id}-${s}`, pos: { ...pos }, startTime: now, radius: radVal, color, effectType: effectiveCfg.collisionEffect, duration: 800 });
                        }
                        
                        if (effectiveCfg.rhythmicSpectrumEnabled) {
                           const hitAngle = Math.atan2(pos.y - centerY, pos.x - centerX);
                           const centerIdx = Math.floor(((hitAngle + Math.PI) / (2 * Math.PI)) * 256) % 256;
                           const spread = effectiveCfg.rhythmicSpectrumCollisionSpread || 10;
                           const intensity = effectiveCfg.rhythmicSpectrumCollisionIntensity || 1.0;
                           for (let j = -spread; j <= spread; j++) {
                               const idx = (centerIdx + j + 256) % 256;
                               const factor = 1 - Math.abs(j) / (spread + 1);
                               persistentSpectrumTarget[idx] = Math.max(persistentSpectrumTarget[idx] || 0, intensity * factor);
                           }
                        }

                        if (effectiveCfg.enableDrawer) {
                            if (!b.hitPoints) b.hitPoints = [];
                            b.hitPoints.push({ 
                                pos: { x: pos.x, y: pos.y }, 
                                time: now,
                                color: b.color,
                                velocity: Math.sqrt(b.vel.x**2 + b.vel.y**2),
                                thickness: effectiveCfg.drawerLineThickness,
                                opacity: effectiveCfg.drawerLineOpacity
                            });
                            const maxLength = effectiveCfg.drawerMaxLength || 1000;
                            if (b.hitPoints.length > maxLength) b.hitPoints.shift();
                        }
                    },
                    isRhythmic
                );
            }

            if (!isRhythmic && !isEscape) {
                b.vel.y += effectiveCfg.gravity * stepTS;
                const airRes = Math.pow(effectiveCfg.airResistance || 1.0, stepTS);
                b.vel.x *= airRes; 
                b.vel.y *= airRes;

                if (effectiveCfg.mode !== 'SPACE_SPIRAL' && !effectiveCfg.enableRhythmic) {
                  const clamped = SpawnerPhysics.clampVelocity(b.vel, effectiveCfg);
                  b.vel.x = clamped.x;
                  b.vel.y = clamped.y;
                }
                
                b.pos.x += b.vel.x * stepTS; b.pos.y += b.vel.y * stepTS;

                const impact = resolveWallCollision(b, centerX, centerY, containerRadiusRef.current, effectiveCfg, morphData, false, (pos, radius, color) => {
                    b.justBounced = true;

                    if (effectiveCfg.spawnOnWallCollision && balls.length < effectiveCfg.maxBalls) {
                        spawns.push({ pos: { ...pos }, color });
                    }

                    const radVal = radius * 2.5 * (effectiveCfg.collisionEffectScale || 1.0);
                    const durVal = effectiveCfg.collisionEffectDuration || 800;
                    if (collisionEventPool) {
                        collisionEvents.push(collisionEventPool.get(
                            `col-wall-${now}-${b.id}-${s}`,
                            pos,
                            now,
                            radVal,
                            color,
                            effectiveCfg.collisionEffect,
                            durVal
                        ));
                    } else {
                        collisionEvents.push({ 
                            id: `col-wall-${now}-${b.id}-${s}`, 
                            pos: { ...pos }, 
                            startTime: now, 
                            radius: radVal, 
                            color, 
                            effectType: effectiveCfg.collisionEffect, 
                            duration: durVal 
                        });
                    }
                    
                    if (effectiveCfg.rhythmicSpectrumEnabled) {
                       const hitAngle = Math.atan2(pos.y - centerY, pos.x - centerX);
                       const centerIdx = Math.floor(((hitAngle + Math.PI) / (2 * Math.PI)) * 256) % 256;
                       const spread = effectiveCfg.rhythmicSpectrumCollisionSpread || 10;
                       const intensity = effectiveCfg.rhythmicSpectrumCollisionIntensity || 1.0;
                       for (let j = -spread; j <= spread; j++) {
                           const idx = (centerIdx + j + 256) % 256;
                           const factor = 1 - Math.abs(j) / (spread + 1);
                           persistentSpectrumTarget[idx] = Math.max(persistentSpectrumTarget[idx] || 0, intensity * factor);
                       }
                    }

                    if (effectiveCfg.ballGrowthMode === 'COLLISION') {
                        const minAllowed = effectiveCfg.ballMinRadius || 1;
                        const growth = ballGrowthRateRef.current;
                        b.radius = Math.max(minAllowed, Math.min(effectiveCfg.ballMaxRadius || 1000, b.radius + growth));
                    }

                    if (effectiveCfg.enableDrawer) {
                        if (!b.hitPoints) b.hitPoints = [];
                        b.hitPoints.push({ 
                            pos: { x: pos.x, y: pos.y }, 
                            time: now,
                            color: b.color,
                            velocity: Math.sqrt(b.vel.x**2 + b.vel.y**2),
                            thickness: effectiveCfg.drawerLineThickness,
                            opacity: effectiveCfg.drawerLineOpacity
                        });
                        const maxLength = effectiveCfg.drawerMaxLength || 1000;
                        if (b.hitPoints.length > maxLength) b.hitPoints.shift();
                    }
                });
                if (impact > 0.1) {
                    b.justBounced = true;
                    playCollisionSound();
                    
                    if (effectiveCfg.speedIncreaseMode === 'COLLISION') {
                        timeScaleRef.current += speedIncreaseRateRef.current;
                    }
                    
                    if (effectiveCfg.containerShrinkMode === 'COLLISION') {
                        const shrink = containerShrinkRateRef.current * 10;
                        containerRadiusRef.current = Math.max(effectiveCfg.minContainerRadius, containerRadiusRef.current - shrink);
                    }
                    
                    if (effectiveCfg.ballGrowthMode === 'COLLISION') {
                        const minAllowed = effectiveCfg.ballMinRadius || 1;
                        const growth = ballGrowthRateRef.current;
                        b.radius = Math.max(minAllowed, Math.min(effectiveCfg.ballMaxRadius || 1000, b.radius + growth));
                    }
                }
            }
        }
        
        if (effectiveCfg.outsideColliderEnabled) {
            resolveOutsideColliderCollision(b, centerX, centerY, effectiveCfg);
        }
      }

      if (!(workerRef?.current && (effectiveCfg.enableSpawner || balls.length > 20))) {
          if (physicsSystem && (effectiveCfg.spawnOnBallCollision || effectiveCfg.enableSpaceSpiral || effectiveCfg.ballGrowthMode === 'COLLISION')) {
              physicsSystem.resolveBallCollisions(balls, effectiveCfg, (b1: Ball, b2: Ball, impact: number) => {
                  const minContainerR = ShapeUtils.getMinRadius(morphData?.from || effectiveCfg.containerShape, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, containerRadiusRef.current, effectiveCfg.containerRadius) - (effectiveCfg.containerLineWidth || 4) / 2;
                  const remainsInside = !effectiveCfg.enableEscape && !effectiveCfg.enableSpaceSpiral;
                  const maxAllowed = remainsInside ? Math.min(effectiveCfg.ballMaxRadius, minContainerR) : (effectiveCfg.ballMaxRadius || 1000);

                  if (effectiveCfg.ballGrowthMode === 'COLLISION') {
                      const growth = ballGrowthRateRef.current;
                      b1.radius = Math.min(maxAllowed, b1.radius + growth);
                      b2.radius = Math.min(maxAllowed, b2.radius + growth);
                  }

                  if (impact > 0.2) {
                      b1.justBounced = true;
                      b2.justBounced = true;
                      playCollisionSound();

                      const canSpawn = balls.length < effectiveCfg.maxBalls;
                      if (effectiveCfg.spawnOnBallCollision && canSpawn && now - (b1.lastSpawnTime || 0) > 150) {
                          const midX = (b1.pos.x + b2.pos.x) / 2;
                          const midY = (b1.pos.y + b2.pos.y) / 2;
                          spawns.push({ pos: { x: midX, y: midY }, color: b1.color });
                          b1.lastSpawnTime = now;
                      }

                      const midX = (b1.pos.x + b2.pos.x) / 2;
                      const midY = (b1.pos.y + b2.pos.y) / 2;
                      const radVal = (b1.radius + b2.radius) * 1.2 * (effectiveCfg.collisionEffectScale || 1.0);
                      const durVal = effectiveCfg.collisionEffectDuration || 800;
                      if (collisionEventPool) {
                          collisionEvents.push(collisionEventPool.get(
                              `col-ball-${now}-${b1.id}-${b2.id}`,
                              { x: midX, y: midY },
                              now,
                              radVal,
                              b1.color,
                              effectiveCfg.collisionEffect,
                              durVal
                          ));
                      } else {
                          collisionEvents.push({ 
                              id: `col-ball-${now}-${b1.id}-${b2.id}`, 
                              pos: { x: midX, y: midY }, 
                              startTime: now, 
                              radius: radVal, 
                              color: b1.color, 
                              effectType: effectiveCfg.collisionEffect, 
                              duration: durVal 
                          });
                      }

                      if (effectiveCfg.speedIncreaseMode === 'COLLISION') {
                          timeScaleRef.current += speedIncreaseRateRef.current;
                      }
                      
                      if (effectiveCfg.containerShrinkMode === 'COLLISION') {
                          const shrink = containerShrinkRateRef.current * 10;
                          containerRadiusRef.current = Math.max(effectiveCfg.minContainerRadius, containerRadiusRef.current - shrink);
                      }
                  }

                  if (effectiveCfg.enableDrawer) {
                      const maxLength = effectiveCfg.drawerMaxLength || 1000;
                      if (!b1.hitPoints) b1.hitPoints = [];
                      b1.hitPoints.push({ 
                          pos: { x: b1.pos.x, y: b1.pos.y }, 
                          time: now,
                          color: b1.color,
                          velocity: Math.sqrt(b1.vel.x**2 + b1.vel.y**2),
                          thickness: effectiveCfg.drawerLineThickness,
                          opacity: effectiveCfg.drawerLineOpacity
                      });
                      if (b1.hitPoints.length > maxLength) b1.hitPoints.shift();
                      
                      if (!b2.hitPoints) b2.hitPoints = [];
                      b2.hitPoints.push({ 
                          pos: { x: b2.pos.x, y: b2.pos.y }, 
                          time: now,
                          color: b2.color,
                          velocity: Math.sqrt(b2.vel.x**2 + b2.vel.y**2),
                          thickness: effectiveCfg.drawerLineThickness,
                          opacity: effectiveCfg.drawerLineOpacity
                      });
                      if (b2.hitPoints.length > maxLength) b2.hitPoints.shift();
                  }
              });
          }
      }
    }
    return spawns;
  }
};
